import { formatLineAmountForDisplay, formatLineFormulaForDisplay, formatMoney } from "./quoteBudgetEditorViewService";
import type { QuoteBudgetLine, QuoteBudgetPeopleCounts } from "../types";
import type { ManualEntryContext } from "../types/manualEntry";
import type { QuoteBudgetWorkspaceGroup } from "./quoteBudgetWorkspaceService";
import { buildManualDraftLines, parseManualEntries } from "./quoteBudgetManualEntryService";
import { manualBusDayRegions } from "./quoteBudgetBusRegionService";

/** 分组标题与辅助说明分别展示，地域不混入日期或内部组名。 */
export type ManualTableGroupDisplay = { label: string; notes: string[] };

/** 从自动/历史大巴行名解析 D 天次，如 `D1 送:`、`D2-D3 佛山-广州`。 */
function legacyBusDaysFromText(text: string): number[] | null {
  const matched = text.trim().match(/^D(\d+)(?:-D(\d+))?/u);
  if (!matched) return null;
  const start = Number(matched[1]);
  const end = Number(matched[2] ?? matched[1]);
  if (!Number.isSafeInteger(start) || !Number.isSafeInteger(end) || start < 1 || end < start) return null;
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

/** 从行名识别单送/单接；无标记时视为当地用车。 */
function legacyBusServiceFromText(text: string): string {
  if (/单接|(?:^|[\s\u3000])接[:：]/u.test(text) || /D\d+(?:-D\d+)?\s*接(?:[:：\s]|$)/u.test(text)) return "单接";
  if (/单送|(?:^|[\s\u3000])送[:：]/u.test(text) || /D\d+(?:-D\d+)?\s*送(?:[:：\s]|$)/u.test(text)) return "单送";
  return "当地用车";
}

/** 优先燃油段名，再组标签与其它行名，得到可展示的天次与服务。 */
function legacyBusGroupScope(group: QuoteBudgetWorkspaceGroup): { days: number[]; service: string } | null {
  const fuel = group.lines.find((line) => line.resourceEntryType === "包车燃油费");
  const texts = [
    fuel?.itemNameSnapshot, fuel?.spuNameSnapshot, group.label,
    ...group.lines.flatMap((line) => [line.itemNameSnapshot, line.spuNameSnapshot]),
  ];
  for (const text of texts) {
    if (!text?.trim()) continue;
    const days = legacyBusDaysFromText(text);
    if (!days?.length) continue;
    return { days, service: legacyBusServiceFromText(text) };
  }
  return null;
}

/** 天次范围说明与新版人工组共用同一口径。 */
function busScopeNote(days: number[], service: string): string {
  const range = days.length === 1 ? `第${days[0]}天`
    : days.every((day, index) => index === 0 || day === days[index - 1] + 1) ? `第${days[0]}至${days.at(-1)}天` : `仅第${days.join("、")}天`;
  return `${range} · ${service}`;
}

/** 只依据真实地域与已保存范围生成标题和说明，不改变分组或费用。 */
export function buildManualTableGroupDisplay(group: QuoteBudgetWorkspaceGroup, context: Pick<ManualEntryContext, "areas" | "itineraryDays"> = {}): ManualTableGroupDisplay {
  const fallback: ManualTableGroupDisplay = { label: group.label, notes: [] };
  const snapshot = group.lines[0]?.entrySnapshot;
  if (group.category === "stay") {
    const areaId = snapshot?.context?.areaId;
    if (areaId && group.lines.some(line => line.entrySnapshot?.context?.areaId !== areaId)) return fallback;
    const matches = context.areas?.filter(area => areaId ? area.id === areaId : area.name === group.label) ?? [];
    const area = matches.length === 1 ? matches[0] : undefined;
    return area && Number.isSafeInteger(area.nights) && area.nights >= 0 ? { ...fallback, notes: [`行程参考 ${area.nights} 晚`] } : fallback;
  }
  if (group.category !== "bus") return fallback;
  if (snapshot?.inputMode === "manual_v2") {
    const { dayNumbers: days, serviceType } = snapshot.context ?? {};
    const service = serviceType && { local_charter: "当地用车", pickup: "单接", dropoff: "单送" }[serviceType];
    if (!service || !days?.length || days.some((day, index) => !Number.isSafeInteger(day) || day < 1 || (index > 0 && day <= days[index - 1]))) return fallback;
    if (group.lines.some(line => line.entrySnapshot?.inputMode !== "manual_v2"
      || line.entrySnapshot.context?.serviceType !== serviceType || line.entrySnapshot.context?.dayNumbers?.join(",") !== days.join(","))) return fallback;
    return { label: snapshot.context?.regionName || buildManualBusRegion(days, context.itineraryDays) || "区域待确认", notes: [busScopeNote(days, service)] };
  }
  // 自动/历史大巴：能从行名解析天次与服务时，与新版组同款两行展示。
  const legacy = legacyBusGroupScope(group);
  if (!legacy) return fallback;
  return {
    label: buildManualBusRegion(legacy.days, context.itineraryDays) || "区域待确认",
    notes: [busScopeNote(legacy.days, legacy.service)],
  };
}

/** 主表和实时预览共用真实地域汇总，过滤占位且不推测缺失地点。 */
export function buildManualBusRegion(days: number[], itineraryDays: ManualEntryContext["itineraryDays"]): string {
  const location = (value?: string) => value?.trim() === "待补充" ? "" : value?.trim() ?? "";
  const places = days.flatMap(day => {
    const matches = itineraryDays?.filter(place => place.day === day) ?? [];
    const place = matches.length === 1 ? matches[0] : undefined;
    return manualBusDayRegions(place).map(region => ({
      province: location(region.provinceName), city: location(region.cityName),
    }));
  }).filter(place => place.province || place.city);
  // 地域仅汇总可确认地点，缺失地域不缩小已保存的完整用车天次。
  const provinceCities = new Map<string, Set<string>>();
  for (const place of places) {
    const cities = provinceCities.get(place.province) ?? new Set<string>();
    if (place.city) cities.add(place.city);
    provinceCities.set(place.province, cities);
  }
  const region = [...new Set(places.map(place => place.province && provinceCities.get(place.province)!.size > 3
    ? place.province : place.city || place.province))].join("—");
  return region;
}

/** 主表只读展示因素；无法确认的旧行保留完整计费依据。 */
export type ManualTableLineDisplay = {
  /** 合并展示行的实际来源；来源还支撑其他车型时不能单独删除。 */
  deleteLineIds?: string[];
  deleteBlocked?: boolean;
  people: string | null;
  factor: string | null;
  factorUnit: string;
  mode: string;
  price: string | null;
  amount: string;
  formula: string;
  compatibility: boolean;
  /** 大巴车型列；新版取快照车型，旧自动行从规格解析。 */
  vehicle: string | null;
};

/** 补齐既有金额格式化结果的小数位，待补价等状态文字原样保留。 */
export function manualTableMoney(text: string): string {
  return text.replace(/(¥-?[\d,]+)(?:\.(\d+))?$/, (_match, integer: string, fraction: string | undefined) => `${integer}.${(fraction ?? "").padEnd(2, "0")}`);
}

/** 空值不伪造成零，非有限或负因素由完整公式兼容展示。 */
function nonnegative(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

/** 主表不展示自动油费估算与原始线段长文，避免挤占车型列。 */
export function isLegacyBusFuelBasisText(text: string | null | undefined): boolean {
  const value = String(text ?? "");
  return value.includes("按车型公里油费估算") || value.includes("原始大巴线段");
}

/** 从规格或计价依据解析车队，保持座位数出现顺序。 */
function parseLegacyBusFleet(line: QuoteBudgetLine): Array<{ seat: number; count: number }> {
  const patterns = [
    /(\d+)座\s*x\s*(\d+)辆/g,
    /(\d+)座(?:按\d+座)?.*?x\s*(\d+)辆/g,
  ];
  const sources = [String(line.itemSpecSnapshot ?? ""), String(line.priceBasisDesc ?? "")];
  for (const source of sources) {
    for (const pattern of patterns) {
      const fleet = Array.from(source.matchAll(pattern))
        .map((match) => ({ seat: Number(match[1]), count: Number(match[2]) }))
        .filter((item) => Number.isSafeInteger(item.seat) && item.seat > 0 && Number.isSafeInteger(item.count) && item.count > 0);
      if (fleet.length) return fleet;
    }
  }
  return [];
}

/** 解析金额为分；非法格式返回 null。 */
function amountToCents(amount: string | number): { sign: string; cents: bigint } | null {
  const matched = String(amount).trim().match(/^(-?)(\d+)(?:\.(\d+))?$/);
  if (!matched) return null;
  return {
    sign: matched[1],
    cents: BigInt(matched[2]) * 100n + BigInt(`${matched[3] ?? ""}00`.slice(0, 2)),
  };
}

/** 分转展示金额文本。 */
function centsToAmountText(sign: string, cents: bigint): string {
  const negative = sign === "-" && cents !== 0n ? "-" : "";
  return `${negative}${cents / 100n}.${String(cents % 100n).padStart(2, "0")}`;
}

/** 金额按辆数均摊为整段单车价展示，半入保留到分；无法整除时仍展示，不改写落库金额。 */
function perVehicleSegmentPrice(amount: string | number, vehicles: number): string | null {
  if (!Number.isSafeInteger(vehicles) || vehicles <= 0) return null;
  const parsed = amountToCents(amount);
  if (!parsed) return null;
  const divisor = BigInt(vehicles);
  const quotient = parsed.cents / divisor;
  const remainder = parsed.cents % divisor;
  const cents = quotient + (remainder * 2n >= divisor ? 1n : 0n);
  return centsToAmountText(parsed.sign, cents);
}

/** 按辆数比例拆分总额到分，尾差落在最后一档，保证合计等于原金额。 */
function allocateAmountByVehicleCounts(amount: string | number, counts: number[]): string[] | null {
  const parsed = amountToCents(amount);
  const totalVehicles = counts.reduce((sum, count) => sum + count, 0);
  if (!parsed || !counts.length || !Number.isSafeInteger(totalVehicles) || totalVehicles <= 0) return null;
  let allocated = 0n;
  return counts.map((count, index) => {
    if (index === counts.length - 1) return centsToAmountText(parsed.sign, parsed.cents - allocated);
    const share = (parsed.cents * BigInt(count)) / BigInt(totalVehicles);
    allocated += share;
    return centsToAmountText(parsed.sign, share);
  });
}

/** 自动大巴主表展开行：多车型按车型分行，操作仍指向原费用行。 */
export type ManualTableBusRow = {
  key: string;
  line: QuoteBudgetLine;
  display: ManualTableLineDisplay;
  showActions: boolean;
};

/** 新版一行一车型；旧自动多车型默认拆成多行，避免再点修改本组才分行。 */
export function buildManualTableBusRows(line: QuoteBudgetLine, people: QuoteBudgetPeopleCounts): ManualTableBusRow[] {
  const display = buildManualTableLineDisplay(line, people);
  if (line.entrySnapshot?.inputMode === "manual_v2" || !["包车", "大巴车"].includes(line.resourceType)) {
    return [{ key: line.budgetLineId, line, display, showActions: true }];
  }
  const fleet = parseLegacyBusFleet(line);
  if (fleet.length <= 1) return [{ key: line.budgetLineId, line, display, showActions: true }];
  const vehicles = fleet.reduce((sum, item) => sum + item.count, 0);
  const quantity = nonnegative(line.quantity);
  const amountText = String(line.budgetAmount ?? "").trim() || (
    quantity !== null && nonnegative(line.budgetUnitPrice) !== null
      ? (quantity * nonnegative(line.budgetUnitPrice)!).toFixed(2) : ""
  );
  const segment = perVehicleSegmentPrice(amountText, vehicles);
  const amounts = allocateAmountByVehicleCounts(amountText, fleet.map((item) => item.count));
  if (!segment || !amounts || display.compatibility) {
    return [{ key: line.budgetLineId, line, display, showActions: true }];
  }
  return fleet.map((item, index) => ({
    key: `${line.budgetLineId}::fleet:${index}`,
    line,
    showActions: true,
    display: {
      ...display,
      vehicle: `${item.seat}座`,
      factor: String(item.count),
      factorUnit: "辆",
      price: line.priceSourceType === "unpriced" ? "待补价" : manualTableMoney(formatMoney(segment)),
      amount: line.priceSourceType === "unpriced" ? "待补价" : manualTableMoney(formatMoney(amounts[index])),
      compatibility: false,
    },
  }));
}

/** 同组自动大巴固定费/燃油费等按车型叠加，避免同车型重复分行。 */
export function buildManualTableBusRowsForGroup(lines: QuoteBudgetLine[], people: QuoteBudgetPeopleCounts): ManualTableBusRow[] {
  const expanded = lines.flatMap((line) => buildManualTableBusRows(line, people)).map(row => ({
    ...row, display: { ...row.display, deleteLineIds: [row.line.budgetLineId] },
  }));
  const manual: ManualTableBusRow[] = [];
  const legacy: ManualTableBusRow[] = [];
  for (const row of expanded) {
    if (row.line.entrySnapshot?.inputMode === "manual_v2") manual.push(row);
    else legacy.push(row);
  }
  if (legacy.length <= 1) return [...manual, ...legacy].map(row => ({
    ...row, display: { ...row.display, deleteBlocked: false },
  }));
  const stacked: ManualTableBusRow[] = [];
  const indexByVehicle = new Map<string, number>();
  for (const row of legacy) {
    const vehicle = row.display.vehicle;
    if (!vehicle || row.display.compatibility || row.display.amount === "待补价") {
      stacked.push({ ...row, showActions: true });
      continue;
    }
    const existing = indexByVehicle.get(vehicle);
    if (existing === undefined) {
      indexByVehicle.set(vehicle, stacked.length);
      stacked.push({
        ...row,
        key: `stack:${vehicle}:${row.line.budgetLineId}`,
        showActions: true,
      });
      continue;
    }
    const target = stacked[existing]!;
    const count = Math.max(Number(target.display.factor) || 0, Number(row.display.factor) || 0);
    const mergedAmount = sumDisplayAmounts(target.display.amount, row.display.amount);
    const mergedPrice = count > 0 && mergedAmount ? perVehicleSegmentPrice(mergedAmount.replace(/[¥,]/g, ""), count) : null;
    stacked[existing] = {
      ...target,
      display: {
        ...target.display,
        deleteLineIds: [...new Set([...(target.display.deleteLineIds ?? []), ...(row.display.deleteLineIds ?? [])])],
        factor: String(count || target.display.factor),
        amount: mergedAmount ? manualTableMoney(formatMoney(mergedAmount)) : target.display.amount,
        price: mergedPrice ? manualTableMoney(formatMoney(mergedPrice)) : target.display.price,
      },
    };
  }
  const result = [...manual, ...stacked];
  return result.map(row => ({ ...row, display: { ...row.display,
    deleteBlocked: row.display.deleteLineIds?.some(id => result.some(other => other !== row && other.display.deleteLineIds?.includes(id))) ?? false,
  } }));
}

/** 叠加两行展示金额；非法格式时返回 null。 */
function sumDisplayAmounts(left: string, right: string): string | null {
  const parsedLeft = amountToCents(left.replace(/[¥,]/g, ""));
  const parsedRight = amountToCents(right.replace(/[¥,]/g, ""));
  if (!parsedLeft || !parsedRight || parsedLeft.sign !== parsedRight.sign) return null;
  return centsToAmountText(parsedLeft.sign, parsedLeft.cents + parsedRight.cents);
}

/** 从已确认行提取主表展示数据，不改变费用与人数联动规则。 */
export function buildManualTableLineDisplay(line: QuoteBudgetLine, people: QuoteBudgetPeopleCounts): ManualTableLineDisplay {
  const fallback: ManualTableLineDisplay = {
    people: null, factor: null, factorUnit: "", mode: "", price: null, vehicle: null,
    amount: line.priceSourceType === "unpriced" ? "待补价" : manualTableMoney(formatLineAmountForDisplay(line)),
    formula: formatLineFormulaForDisplay(line), compatibility: true,
  };
  const snapshot = line.entrySnapshot;
  const numericPrice = Number(line.budgetUnitPrice);
  const railFare = line.itemSpecSnapshot === "铁路票价";
  const price = line.priceSourceType === "unpriced"
    ? "待补价"
    : Number.isFinite(numericPrice) && numericPrice === 0
      ? "免费"
      : manualTableMoney(formatMoney(line.budgetUnitPrice));
  const quantity = nonnegative(line.quantity);
  const fixedPeople = nonnegative(line.manualPeopleCount);
  const rolePeople: Record<string, number | undefined> = {
    学生: people.studentCount, 老师: people.teacherCount, 导游: people.guideCount ?? 0, 司机: people.driverCount ?? 0,
  };
  const linkedPeople = nonnegative(rolePeople[line.travelerRole ?? ""]);
  const factorUnit = line.unit === "人晚" ? "晚" : line.unit === "人天" ? "天" : line.unit === "人餐" ? "餐" : line.unit === "人" ? "" : "次";
  const busVehicle = snapshot?.context?.vehicleSpec ?? line.skuNameSnapshot ?? line.itemNameSnapshot ?? null;
  if (snapshot?.inputMode === "manual_v2") {
    if (snapshot.quantityMode === "total") return { ...fallback, price, vehicle: busVehicle, mode: "整项金额", compatibility: false };
    if (snapshot.quantityMode === "quantity") return {
      ...fallback, price, vehicle: busVehicle, factor: quantity === null ? null : String(quantity), factorUnit: line.unit,
      mode: line.unit === "场" ? "按场次" : line.unit === "桌" ? "按桌" : "固定数量", compatibility: quantity === null,
    };
    const factor = nonnegative(snapshot.billingCount);
    if (!factor) return fallback;
    const count = snapshot.quantityMode === "linked_people" ? linkedPeople : fixedPeople ?? (quantity === null ? null : quantity / factor);
    if (count === null) {
      return railFare
        ? { ...fallback, price, people: null, amount: "—", mode: "随人数", compatibility: false }
        : fallback;
    }
    const railAmount = railFare && Number.isFinite(numericPrice)
      ? manualTableMoney(formatMoney((count * factor * numericPrice).toFixed(2)))
      : fallback.amount;
    return { ...fallback, price, vehicle: busVehicle, people: String(count), factor: String(factor), factorUnit,
      amount: railAmount,
      mode: snapshot.quantityMode === "linked_people" ? "随人数" : "固定人数", compatibility: false };
  }
  // V1 自动包车：能解析车队时按「车型 / 辆数 / 整段单车价」列展示；金额按辆数均摊仅用于展示。
  if (["包车", "大巴车"].includes(line.resourceType)) {
    const fleet = parseLegacyBusFleet(line);
    const vehicles = fleet.reduce((sum, item) => sum + item.count, 0);
    const amountText = String(line.budgetAmount ?? "").trim() || (
      quantity !== null && nonnegative(line.budgetUnitPrice) !== null
        ? (quantity * nonnegative(line.budgetUnitPrice)!).toFixed(2) : ""
    );
    const segment = perVehicleSegmentPrice(amountText, vehicles);
    if (fleet.length && vehicles > 0 && segment) {
      return {
        ...fallback,
        vehicle: fleet.map((item) => `${item.seat}座`).join("+"),
        factor: String(vehicles),
        factorUnit: "辆",
        price: line.priceSourceType === "unpriced" ? "待补价" : manualTableMoney(formatMoney(segment)),
        mode: "固定数量",
        compatibility: false,
      };
    }
    return fallback;
  }
  // 旧餐未独立保存餐数，不能用当前联动人数倒推；固定人数只拆分完整正整数餐数。
  if (line.resourceType === "餐" && (line.unit !== "人餐" || line.isLinkedToPeopleCount
    || !fixedPeople || !Number.isSafeInteger(fixedPeople) || quantity === null || !Number.isSafeInteger(quantity / fixedPeople))) return fallback;
  if (line.unit === "项") return { ...fallback, price, mode: "整项金额", compatibility: false };
  if (line.unit === "场" && quantity !== null) return { ...fallback, price, factor: String(quantity), factorUnit: "场", mode: "按场次", compatibility: false };
  const count = line.isLinkedToPeopleCount ? linkedPeople : fixedPeople;
  const feeTimes = line.resourceEntryType === "手续费" ? nonnegative(line.itemSpecSnapshot?.match(/^(\d+)次$/)?.[1]) : null;
  const explicitFactor = line.unit === "人晚" ? nonnegative(line.nightCount)
    : line.unit === "人天" ? nonnegative(line.dayCount)
      : feeTimes ?? (line.unit === "人" || (line.resourceType === "大交通" && line.resourceEntryType !== "手续费") ? 1 : null);
  const factor = explicitFactor ?? (count && quantity !== null ? quantity / count : null);
  if (!factor || quantity === null) return fallback;
  const resolvedPeople = count ?? quantity / factor;
  return { ...fallback, price, people: String(resolvedPeople), factor: String(factor), factorUnit,
    mode: line.isLinkedToPeopleCount ? "随人数" : "固定人数", compatibility: false };
}

/**
 * 行级修改弹窗是否应对当前展示行打开。
 * 同费用多车型分行共用 lineId，必须再用 rowKey 锚定；旧缓存无 rowKey 时仅匹配 key 等于 lineId 的行。
 */
export function isManualFormulaPopoverOpen(
  formula: { lineId: string; rowKey?: string } | null | undefined,
  lineId: string,
  rowKey: string,
): boolean {
  if (!formula || formula.lineId !== lineId) return false;
  return (formula.rowKey ?? lineId) === rowKey;
}

/**
 * 大巴行级小弹窗回显：有车型列时用「辆数 × 整段单车价」，与主表一致；
 * 否则返回 null，由调用方回退 buildLineFormula。
 */
export function buildBusDisplayInlineFormula(display: ManualTableLineDisplay): string | null {
  if (!display.vehicle || display.compatibility || !display.factor) return null;
  if (display.factorUnit && display.factorUnit !== "辆") return null;
  if (!display.price || display.price === "待补价") return null;
  const price = display.price === "免费" ? "0" : display.price.replace(/[¥,]/g, "");
  if (!price || !/^\d+(?:\.\d+)?$/u.test(price)) return null;
  if (!/^[1-9]\d*$/u.test(display.factor)) return null;
  return `${display.factor}辆 x ${price}元`;
}

/** 将合并展示的旧大巴组完整转为人工行，再仅修改目标车型；不可精确还原时拒绝部分写入。 */
export function applyLegacyBusDisplayFormula(
  group: QuoteBudgetWorkspaceGroup,
  rowKey: string,
  text: string,
  context: ManualEntryContext,
): { lines: QuoteBudgetLine[]; replaceIds: string[]; error: string | null } {
  const fail = (error: string) => ({ lines: [], replaceIds: [], error });
  const legacy = group.lines.filter(line => line.entrySnapshot?.inputMode !== "manual_v2");
  if (legacy.some(line => line.isLocked)) return fail("本组含锁定费用，无法修改");
  const rows = buildManualTableBusRowsForGroup(legacy, context.people);
  const target = rows.find(row => row.key === rowKey);
  if (!target) return fail("当前车型行已变化，请关闭后重试");
  const initial = buildBusDisplayInlineFormula(target.display);
  if (initial === text) return { lines: [], replaceIds: [], error: null };
  const scope = legacyBusGroupScope({ ...group, lines: legacy });
  if (!scope) return fail("原用车范围无法还原，请使用修改本组核对");
  const range = scope.days.length > 1 && scope.days.every((day, index) => index === 0 || day === scope.days[index - 1] + 1)
    ? `${scope.days[0]}-${scope.days.at(-1)}` : scope.days.join(",");
  const records: string[] = [];
  for (const row of rows) {
    const formula = buildBusDisplayInlineFormula(row.display);
    if (!formula) return fail("本组存在无法还原的费用，请使用修改本组核对");
    if (row.key !== rowKey) {
      const price = amountToCents(row.display.price === "免费" ? "0" : row.display.price.replace(/[¥,]/g, ""));
      const amount = amountToCents(row.display.amount === "免费" ? "0" : row.display.amount.replace(/[¥,]/g, ""));
      if (!price || !amount || price.cents * BigInt(row.display.factor!) !== amount.cents) {
        return fail("其他车型存在均摊尾差，请使用修改本组核对");
      }
    }
    records.push(`${range} ${scope.service} ${row.display.vehicle} ${row.key === rowKey ? text : formula}`);
  }
  const parsed = parseManualEntries(records.join("\n"), context);
  if (!parsed.canConfirm) return fail(parsed.items.flatMap(item => item.issues.map(issue => issue.message)).join("；") || "请填写完整公式");
  return { lines: buildManualDraftLines(parsed), replaceIds: legacy.map(line => line.budgetLineId), error: null };
}
